import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Loader2, X, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  title: "",
  excerpt: "",
  meta_description: "",
  cover_image: "",
  tags: "",
  content: "",
  published: true,
};

const inputCls =
  "w-full bg-background border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-warm focus:ring-1 focus:ring-warm transition-colors";

export default function AdminBlog() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, ...form}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/admin/blog");
      setPosts(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) =>
    setEditing({
      id: p.id,
      title: p.title,
      excerpt: p.excerpt,
      meta_description: p.meta_description,
      cover_image: p.cover_image,
      tags: p.tags.join(", "),
      content: p.content,
      published: p.published,
    });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: editing.title,
      excerpt: editing.excerpt,
      meta_description: editing.meta_description,
      cover_image: editing.cover_image,
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      content: editing.content,
      published: editing.published,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Article mis à jour");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Article créé");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer définitivement l'article « ${p.title} » ?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Article supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen grain relative">
      <header className="border-b border-border sticky top-0 bg-background/70 backdrop-blur-2xl z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-black text-lg tracking-tighter">GMP<span className="text-accent">.</span></span>
            <nav className="flex items-center gap-4 text-sm">
              <Link to="/admin" className="text-muted-foreground hover:text-foreground transition-colors">Demandes</Link>
              <Link to="/admin/blog" className="text-warm font-medium">Articles</Link>
              <Link to="/admin/cockpit" className="text-muted-foreground hover:text-foreground transition-colors">Cockpit Avis</Link>
            </nav>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10 relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl">Articles du blog</h1>
          <button
            onClick={openNew}
            data-testid="blog-new-button"
            className="inline-flex items-center gap-2 bg-warm text-background font-semibold px-4 py-2.5 hover:bg-warm-soft transition-colors"
          >
            <Plus size={18} /> Nouvel article
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24 border border-border bg-surface">
            <Inbox size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucun article. Créez le premier.</p>
          </div>
        ) : (
          <div className="border border-border bg-surface divide-y divide-border" data-testid="admin-blog-list">
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-blog-row-${p.id}`} className="p-5 flex items-center gap-4">
                <img src={p.cover_image} alt="" className="w-20 h-14 object-cover border border-border shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-foreground truncate">{p.title}</span>
                    {p.published ? (
                      <span className="font-mono text-[9px] tracking-wider uppercase bg-accent/15 text-accent px-2 py-0.5">Publié</span>
                    ) : (
                      <span className="font-mono text-[9px] tracking-wider uppercase bg-white/10 text-muted-foreground px-2 py-0.5">Brouillon</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">/blog/{p.slug} · {p.reading_time} min</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 border border-border hover:border-foreground/40 transition-colors" title="Voir">
                    <ExternalLink size={15} />
                  </a>
                  <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="p-2 border border-border hover:border-warm/50 hover:text-warm transition-colors" title="Modifier">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`} className="p-2 border border-border hover:border-destructive/60 hover:text-destructive transition-colors" title="Supprimer">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-surface border border-border w-full max-w-2xl my-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
              <h2 className="font-heading font-semibold text-lg">{editing.id ? "Modifier l'article" : "Nouvel article"}</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Titre *</label>
                <input required data-testid="blog-form-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} placeholder="Titre de l'article" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Extrait *</label>
                <textarea required rows={2} data-testid="blog-form-excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={`${inputCls} resize-none`} placeholder="Résumé court (accroche)" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Meta description (SEO) *</label>
                <textarea required rows={2} data-testid="blog-form-meta" value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className={`${inputCls} resize-none`} placeholder="Description pour Google (max 320 car.)" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Image de couverture (URL) *</label>
                  <input required data-testid="blog-form-cover" value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className={inputCls} placeholder="https://…" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Tags (séparés par virgule)</label>
                  <input data-testid="blog-form-tags" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inputCls} placeholder="Avis Google, NFC" />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Contenu (Markdown) *</label>
                <textarea required rows={12} data-testid="blog-form-content" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className={`${inputCls} resize-y font-mono text-xs`} placeholder="## Titre&#10;&#10;Votre contenu en markdown…" />
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground/80">
                <input type="checkbox" data-testid="blog-form-published" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#00e5ff]" />
                Publier l'article
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} data-testid="blog-form-save" className="flex-1 bg-warm text-background font-semibold px-6 py-3 flex items-center justify-center gap-2 hover:bg-warm-soft transition-colors disabled:opacity-60">
                  {saving && <Loader2 size={18} className="animate-spin" />}
                  {editing.id ? "Enregistrer" : "Créer l'article"}
                </button>
                <button type="button" onClick={() => setEditing(null)} className="px-6 py-3 border border-border text-foreground hover:border-foreground/40 transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
